import React from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useTranslation } from 'react-i18next';
import { HUB_TYPES } from '../utils/hubRoutes';
import { getHubSectionStyles } from '../theme/hubThemes';

const HUB_ICONS = {
    [HUB_TYPES.PROJECT_MANAGEMENT]: BusinessCenterIcon,
    [HUB_TYPES.VENDOR]: StorefrontIcon,
    [HUB_TYPES.CUSTOMER]: HomeWorkIcon,
};

function OrganizationRow({
    organization,
    isManaged = false,
    isOwner,
    allowCreateManaged = false,
    onSelect,
    onCreateManaged,
    styles,
}) {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                pl: isManaged ? 1.5 : 0,
                py: 0.75,
                borderBottom: 1,
                borderColor: styles.divider,
                '&:last-child': { borderBottom: 0 },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: styles.bodyText }}
                    noWrap
                    title={organization.name}
                >
                    {organization.name}
                </Typography>
                {isManaged && (
                    <Typography variant="caption" sx={{ color: styles.bodyMuted }} display="block" noWrap>
                        {t('hubs.managedOrganization')}
                    </Typography>
                )}
            </Box>
            <Button
                size="small"
                onClick={() => onSelect(organization)}
                sx={{
                    flexShrink: 0,
                    color: styles.rowButtonColor,
                    fontWeight: 600,
                    minWidth: 'auto',
                    px: 1,
                }}
            >
                {t('hubs.open')}
            </Button>
            {allowCreateManaged && !isManaged && isOwner && organization.managed_by == null && (
                <Button
                    size="small"
                    onClick={() => onCreateManaged(organization)}
                    title={t('hubs.createManaged')}
                    sx={{
                        flexShrink: 0,
                        minWidth: 32,
                        color: styles.rowButtonColor,
                    }}
                >
                    +
                </Button>
            )}
        </Box>
    );
}

export default function HubSelectionSection({
    hubType,
    title,
    description,
    organizations,
    currentUserId,
    darkMode,
    onSelect,
    onCreateTopLevel,
    onCreateManaged,
}) {
    const { t } = useTranslation();
    const styles = getHubSectionStyles(hubType, darkMode);
    const sectionOrgs = organizations.filter((org) => org.organization_type === hubType);
    const allowCreateManaged = hubType === HUB_TYPES.PROJECT_MANAGEMENT;
    const HubIcon = HUB_ICONS[hubType] || BusinessCenterIcon;

    const createLabel = (() => {
        switch (hubType) {
            case HUB_TYPES.VENDOR:
                return t('hubs.createVendorProfile');
            case HUB_TYPES.CUSTOMER:
                return t('hubs.createCustomerProfile');
            default:
                return t('hubs.createOrganization');
        }
    })();

    const sectionListLabel = hubType === HUB_TYPES.PROJECT_MANAGEMENT
        ? t('hubs.yourOrganizations')
        : t('hubs.yourProfiles');

    return (
        <Box
            sx={{
                flex: '1 1 0',
                minWidth: 240,
                maxWidth: 360,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                overflow: 'hidden',
                border: 2,
                borderColor: styles.border,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
        >
            <Box
                sx={{
                    bgcolor: styles.headerBg,
                    color: styles.headerText,
                    px: 2,
                    py: 2,
                    textAlign: 'center',
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
                    <HubIcon sx={{ fontSize: 28, color: styles.headerText }} />
                    <Typography variant="h6" fontWeight={700} sx={{ color: styles.headerText }}>
                        {title}
                    </Typography>
                </Stack>
                <Typography
                    variant="body2"
                    sx={{ color: styles.headerMuted, lineHeight: 1.5, fontSize: '0.8125rem' }}
                >
                    {description}
                </Typography>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: styles.bodyBg,
                    color: styles.bodyText,
                    px: 1.5,
                    py: 1.5,
                    minHeight: 140,
                }}
            >
                <Typography
                    variant="overline"
                    sx={{ color: styles.bodyMuted, letterSpacing: 1, mb: 0.5, display: 'block' }}
                >
                    {sectionListLabel}
                </Typography>

                <Box sx={{ flex: 1, mb: 1.5 }}>
                    {sectionOrgs.length === 0 ? (
                        <Typography
                            variant="body2"
                            sx={{ color: styles.bodyMuted, textAlign: 'center', py: 2 }}
                        >
                            {t('hubs.emptySection')}
                        </Typography>
                    ) : (
                        sectionOrgs.map((org) => (
                            <Box key={org.id}>
                                <OrganizationRow
                                    organization={org}
                                    isOwner={org.created_by === currentUserId}
                                    allowCreateManaged={allowCreateManaged}
                                    onSelect={onSelect}
                                    onCreateManaged={onCreateManaged}
                                    styles={styles}
                                />
                                {(org.managed_organizations || []).map((child) => (
                                    <OrganizationRow
                                        key={child.id}
                                        organization={child}
                                        isManaged
                                        isOwner={false}
                                        allowCreateManaged={false}
                                        onSelect={onSelect}
                                        onCreateManaged={onCreateManaged}
                                        styles={styles}
                                    />
                                ))}
                            </Box>
                        ))
                    )}
                </Box>

                <Button
                    variant="contained"
                    size="medium"
                    fullWidth
                    disableElevation
                    onClick={() => onCreateTopLevel(hubType)}
                    sx={{
                        bgcolor: styles.buttonBg,
                        color: styles.buttonText,
                        fontWeight: 600,
                        py: 1,
                        '&:hover': { bgcolor: styles.buttonHover },
                    }}
                >
                    {createLabel}
                </Button>
            </Box>
        </Box>
    );
}
